/**
 * Dashboard-level filters that cascade to every widget's report request.
 * Mirrors flank-20's dashboard filter bar, adapted to ribeye-api's postReport
 * filter keys (all IN-based). See FILTER_COLUMNS in the FlankReporting query
 * layer for the authoritative key list.
 */
import type { FilterGroup, ReportRequest } from '@/store/slices/reportingV2/reportingV2.api';
import { RELATIVE_PRESET_DAYS } from '../../ReportingV2/components/DateRangeControl';
import {
  type ChannelOption,
  DEVICE_DISPLAY,
  isConversionsPerPlatformScope,
  isPerPlatformScope,
} from '../../ReportingV2/format';
import type { OptionsScope } from '../../ReportingV2/hooks/optionsBatcher';
import { isChannelSpecificMetric, isConversionMetric, isReachMetric } from './canonicalMetrics';
import type { CustomDimensionDef } from './tenantFields';

/** The dashboard-filter keys a keyword/contains search can apply to — every
 *  exact-match key above except clientIds (narrows tenancy, not a WHERE
 *  filter the backend's `contains` mechanism covers). Matches ribeye-api's
 *  `FilterColumnKey` in queryBuilder.ts. */
export type ContainsFilterKey = Exclude<WidgetFilterKey, 'clientIds'>;

export interface DashboardFilters {
  dateStart: string;
  dateEnd: string;
  /**
   * The label of the preset the range came from ('Last 30 Days', 'Custom', …).
   * Purely presentational for the query — it is what lets `refreshRelativeRange`
   * tell "the last 30 days" from "1–30 June", which have identical spans.
   */
  datePreset: string;
  dsps: string[];
  /**
   * Admin-only (flank#5058, same as Unified Reporting's #4454): narrows which
   * white label's Client/Advertiser/Campaign/Line Item options and delivery a
   * dashboard shows. Narrows via the tenancy seam in the API, not a WHERE
   * clause — same as clientIds below.
   */
  whitelabelIds: string[];
  /** Narrows via the tenancy seam in the API, not a WHERE clause — same as Unified Reporting. */
  clientIds: string[];
  advertiserIds: string[];
  campaignIds: string[];
  lineItemIds: string[];
  channelIds: string[];
  deviceIds: string[];
  productIds: string[];
  creativeIds: string[];
  publishers: string[];
  /**
   * Keyword/wildcard filtering (FB-007) — a parallel, ADDITIVE set of terms per
   * dimension: a dimension with BOTH an exact list above and a contains term
   * here must satisfy both (AND), same as the backend compiles them. One term
   * per dimension today (a single keyword search box per filter), not a full
   * multi-term/boolean builder.
   */
  containsFilters: Partial<Record<ContainsFilterKey, string[]>>;
  /** Same shape as `containsFilters`, but excluding matches instead. */
  excludesFilters: Partial<Record<ContainsFilterKey, string[]>>;
  /**
   * Custom-dimension filtering (FB-007) — selected bucket labels, keyed by the
   * custom dimension's own `key`. Only dimensions built on an enum-like canonical
   * field (channel/dsp/device/product/publisher) are supported: a bucket's
   * `values` are the field's own raw warehouse values, so they translate
   * directly into the matching WidgetFilterKey's ID array (see
   * CUSTOM_DIMENSION_FILTER_TARGETS / expandCustomDimensionFilters) with no
   * backend change. Dimensions on entity-name fields (advertiser_name etc.)
   * aren't supported yet — those values aren't database IDs.
   */
  customDimensionFilters: Record<string, string[]>;
  /**
   * Boolean filter groups (FB-007): unlike every filter above, a group can OR
   * conditions across DIFFERENT dimensions (e.g. channel contains "CTV" OR
   * device is "Mobile") — the one composition none of the fields above can
   * express, since they're all AND'd together. One flat AND/OR per group, no
   * nesting; multiple groups AND with each other and with every filter above.
   * Dashboard-level only — not per-widget-overridable (no ticket ask for that).
   */
  booleanGroups: BooleanFilterGroup[];
}

export type BooleanFilterOp = 'is' | 'is_not' | 'contains' | 'not_contains';

export interface BooleanFilterCondition {
  key: ContainsFilterKey;
  op: BooleanFilterOp;
  values: string[];
  /**
   * Local to the builder UI, never sent to the backend (see booleanGroupsToRequest
   * below) — a stable React key so removing a middle condition doesn't reuse
   * another row's component instance, and with it, its half-typed input state
   * (review, flank#4515). Optional so groups saved before this field existed
   * still satisfy the type; the builder falls back to the array index for those.
   */
  id?: string;
}

export interface BooleanFilterGroup {
  op: 'AND' | 'OR';
  conditions: BooleanFilterCondition[];
  /** Same purpose as BooleanFilterCondition.id, one level up. */
  id?: string;
}

const ymd = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const daysAgo = (n: number): string => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return ymd(d);
};

export const DEFAULT_DATE_PRESET = 'Last 30 Days';

export const rangeForDays = (days: number): { dateStart: string; dateEnd: string } => ({
  dateStart: daysAgo(days - 1),
  dateEnd: ymd(new Date()),
});

export const defaultDashboardFilters = (): DashboardFilters => ({
  ...rangeForDays(RELATIVE_PRESET_DAYS[DEFAULT_DATE_PRESET]),
  datePreset: DEFAULT_DATE_PRESET,
  dsps: [],
  whitelabelIds: [],
  clientIds: [],
  advertiserIds: [],
  campaignIds: [],
  lineItemIds: [],
  channelIds: [],
  deviceIds: [],
  productIds: [],
  creativeIds: [],
  publishers: [],
  containsFilters: {},
  excludesFilters: {},
  customDimensionFilters: {},
  booleanGroups: [],
});

/**
 * Conversion-display rule — Prod/Eng office hours, 2026-07-28.
 *
 * Conversion metrics are shown ONLY when exactly one DSP is selected. Conversions
 * are not deduplicated across DSPs: each DSP counts conversions against its own
 * pixel/tag, so one real-world conversion can be claimed by DV360 and Beeswax
 * both. Summing them double-counts, and every conversion-denominated metric
 * (CPA, ROAS) inherits the error. Isolating a single DSP makes the number
 * attributable, so it is safe to display.
 *
 * Zero DSPs selected means "all DSPs", which is the aggregate case this rule
 * exists to suppress — hence `=== 1` rather than `<= 1`.
 *
 * A custom-dimension bucket selection (FB-007) that resolves to a single DSP
 * is exactly as attributable as picking that DSP directly, so it counts the
 * same way here — the expansion mirrors applyDashboardFilters' own union.
 *
 * A Boolean group (also FB-007) can pin the SAME key just as hard as the flat
 * filter above, but only when its own logic can't let a second value through:
 * a positive `is` condition with one value, inside an AND group (every
 * condition must hold) or a one-condition group (AND/OR are equivalent with a
 * single term). An OR alternative, `is_not`, or `contains` never proves a
 * singleton — ignoring that restriction previously suppressed valid metrics
 * behind a group that was, in fact, already isolating one DSP/channel
 * (CodeRabbit, flank#4515).
 */
const singletonGroupValues = (groups: BooleanFilterGroup[], key: ContainsFilterKey): string[] =>
  groups.flatMap(group => {
    if (group.op === 'OR' && group.conditions.length > 1) return [];
    return group.conditions
      .filter(condition => condition.key === key && condition.op === 'is' && condition.values.length === 1)
      .map(condition => condition.values[0]);
  });

/**
 * `dims` is the widget's own active breakdown dimension(s) (e.g. `['dsp']`
 * for a By Platform table) — distinct from `customDimensions`, the tenant's
 * CUSTOM DIMENSION DEFINITIONS used only to expand `customDimensionFilters`
 * above. Reuses Unified Reporting's own `isConversionsPerPlatformScope`
 * (flank#5052): a widget broken down BY dsp is safe row-by-row regardless of
 * the dsp FILTER, and a single filtered dsp from its verified
 * co-occurrence-safe set is safe under any OTHER breakdown too — the same
 * refinement Unified Reporting already shipped that this file's rule had
 * deliberately deferred (see reachVisible below).
 */
export const conversionsVisible = (
  f: DashboardFilters,
  customDimensions: CustomDimensionDef[] = [],
  dims: string[] = [],
): boolean => {
  const expanded = expandCustomDimensionFilters(f.customDimensionFilters ?? {}, customDimensions);
  const groupSingletons = singletonGroupValues(f.booleanGroups ?? [], 'dsps');
  const dspSet = new Set([...(f.dsps ?? []), ...(expanded.dsps ?? []), ...groupSingletons]);
  // Any single filtered dsp is safe with no further breakdown (the existing
  // rule, sound because the whole result set is already that one platform);
  // isConversionsPerPlatformScope only has to add the breakdown-by-dsp case.
  if (dspSet.size === 1) return true;
  return isConversionsPerPlatformScope(undefined, dims);
};

/**
 * Vanity-metric rule — same office hours, same shape.
 *
 * CPCV / LTR aren't comparable across channels (CPCV only exists on video),
 * so they stay hidden until a single channel is isolated. CTR and VCR used to
 * be gated here too, but Unified Reporting's own headline KPI strip shows
 * both completely unconditionally — no channel-singleton check anywhere in
 * `KpiStrip.tsx` — so gating them here disagreed with the screen this is
 * meant to match (flank#5052); VCR's own ratio (completes/plays) already
 * self-excludes non-video delivery from both terms, so blending channels
 * doesn't dilute it the way a raw percentage would, and CTR's possible
 * dilution on non-clickable inventory is Unified Reporting's own accepted
 * number today (whether to fix that is #4752, not this ticket). Same
 * custom-dimension expansion (and Boolean-group singleton detection) as
 * conversionsVisible above.
 */
export const vanityMetricsVisible = (f: DashboardFilters, customDimensions: CustomDimensionDef[] = []): boolean => {
  const expanded = expandCustomDimensionFilters(f.customDimensionFilters ?? {}, customDimensions);
  const groupSingletons = singletonGroupValues(f.booleanGroups ?? [], 'channelIds');
  return new Set([...(f.channelIds ?? []), ...(expanded.channelIds ?? []), ...groupSingletons]).size === 1;
};

/**
 * Reach/Frequency visibility rule (flank#4460), now dimension-aware
 * (flank#5052) — same shape as `conversionsVisible`: a single filtered dsp
 * with no breakdown is safe outright (the whole result set is that one
 * platform); otherwise reuse Unified Reporting's own, already-verified
 * `isPerPlatformScope` (flank#4479) for the breakdown-by-dsp case and the
 * co-occurrence-safe-dsp-under-another-breakdown case, rather than
 * re-deriving or guessing at the safe-DSP list a second time. Same
 * Boolean-group singleton detection as conversionsVisible/vanityMetricsVisible
 * above (CodeRabbit, flank#4515).
 */
export const reachVisible = (
  f: DashboardFilters,
  customDimensions: CustomDimensionDef[] = [],
  dims: string[] = [],
): boolean => {
  const expanded = expandCustomDimensionFilters(f.customDimensionFilters ?? {}, customDimensions);
  const groupSingletons = singletonGroupValues(f.booleanGroups ?? [], 'dsps');
  const dspSet = new Set([...(f.dsps ?? []), ...(expanded.dsps ?? []), ...groupSingletons]);
  // Any single filtered dsp is safe with no further breakdown (the existing
  // rule — the whole result set is already that one platform); isPerPlatformScope
  // only has to add the breakdown-by-dsp case when that's not true.
  if (dspSet.size === 1) return true;
  return isPerPlatformScope(undefined, dims);
};

export type MetricSuppression = 'conversion-multi-dsp' | 'vanity-multi-channel' | 'reach-multi-dsp';

/**
 * Why a metric can't be displayed under these filters, or null if it can.
 * `dims` is the widget's own active breakdown dimension key(s) — pass
 * `['dsp']`/`['channel']` etc. when the widget groups by that dimension, so
 * the dimension-aware refinements in `conversionsVisible`/`reachVisible`
 * apply; omit it (defaults to none) for a widget with no breakdown, where the
 * plain filter-based rule already covers every case correctly (flank#5052).
 */
export function metricSuppression(
  key: string,
  f: DashboardFilters,
  customDimensions: CustomDimensionDef[] = [],
  dims: string[] = [],
): MetricSuppression | null {
  if (isConversionMetric(key) && !conversionsVisible(f, customDimensions, dims)) return 'conversion-multi-dsp';
  if (isChannelSpecificMetric(key) && !vanityMetricsVisible(f, customDimensions)) return 'vanity-multi-channel';
  if (isReachMetric(key) && !reachVisible(f, customDimensions, dims)) return 'reach-multi-dsp';
  return null;
}

export const SUPPRESSION_NOTES: Record<MetricSuppression, string> = {
  'conversion-multi-dsp':
    'Conversions are only shown for a single DSP — across DSPs they are not deduplicated, so a combined total would double-count.',
  'vanity-multi-channel':
    'CPCV is only shown for a single channel — it only exists on video, so the combined view stays on impressions, spend, CPM, reach and frequency.',
  'reach-multi-dsp':
    'Reach and Frequency are only shown for a single DSP — each platform reports its own audience, so a combined total would double-count anyone reached on more than one.',
};

/** @deprecated prefer SUPPRESSION_NOTES — kept for the conversion-only call sites. */
export const CONVERSION_SUPPRESSED_NOTE = SUPPRESSION_NOTES['conversion-multi-dsp'];

/**
 * Stored filters keep an ABSOLUTE date range, so a dashboard saved last week
 * reopens on last week's window while the pill still says "Last 30 Days".
 * Re-anchor to today when the stored preset is a relative one — a dashboard that
 * meant "the last 30 days" should mean that whenever it's opened.
 *
 * Keyed on the STORED LABEL, not the span: "1–30 June" and "Last 30 Days" are
 * both 30 days wide, and only the second one should move. 'Custom', 'This Month'
 * and 'Last Month' are all absolute in this sense and stay pinned.
 */
export function refreshRelativeRange(f: DashboardFilters): DashboardFilters {
  const days = RELATIVE_PRESET_DAYS[f.datePreset];
  return days ? { ...f, ...rangeForDays(days) } : f;
}

/**
 * Merge stored filters over the defaults, recovering `datePreset` for dashboards
 * saved before it existed. Without this they'd inherit the DEFAULT preset and
 * `refreshRelativeRange` would snap a saved custom window to the last 30 days —
 * silently discarding the range someone chose. Falling back to the old
 * span-matching rule keeps those dashboards behaving exactly as they did.
 */
export function normalizeStoredFilters(stored: Partial<DashboardFilters>): DashboardFilters {
  const merged = { ...defaultDashboardFilters(), ...stored };
  // Groups/conditions saved before `id` existed reach the builder with none,
  // which falls back to the array index for its React key — removing an
  // earlier row then reuses a mounted input and its stale draft text can leak
  // into another row (CodeRabbit, flank#4515). Backfill once here, the single
  // funnel both dashboard loading and saved-view selection go through.
  merged.booleanGroups = merged.booleanGroups.map(group => ({
    ...group,
    id: group.id ?? crypto.randomUUID(),
    conditions: group.conditions.map(condition => ({ ...condition, id: condition.id ?? crypto.randomUUID() })),
  }));
  if (stored.datePreset) return merged;

  const start = new Date(`${merged.dateStart}T00:00:00`);
  const end = new Date(`${merged.dateEnd}T00:00:00`);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return merged;
  const spanDays = Math.round((end.getTime() - start.getTime()) / 86_400_000) + 1;
  const label = Object.keys(RELATIVE_PRESET_DAYS).find(k => RELATIVE_PRESET_DAYS[k] === spanDays);
  return { ...merged, datePreset: label ?? 'Custom' };
}

/** The equal-length window immediately before [start, end] — for KPI deltas. */
export const priorRange = (start: string, end: string): { start: string; end: string } => {
  const s = new Date(`${start}T00:00:00`);
  const e = new Date(`${end}T00:00:00`);
  const lenDays = Math.round((e.getTime() - s.getTime()) / 86_400_000) + 1;
  const priorEnd = new Date(s);
  priorEnd.setDate(s.getDate() - 1);
  const priorStart = new Date(priorEnd);
  priorStart.setDate(priorEnd.getDate() - (lenDays - 1));
  return { start: ymd(priorStart), end: ymd(priorEnd) };
};

/**
 * The same calendar dates one year before [start, end] — for a KPI card set to
 * "Compare to: Prior year" (flank#5050; the option existed, but the query
 * behind it silently reused `priorRange` above, so only the label changed).
 * Shifts the YEAR only, not a fixed 365-day offset — a day-count shift drifts
 * away from "the same dates" across any Feb 29 in between. A start or end
 * that IS Feb 29 has no equivalent in a non-leap target year; `Date`'s own
 * rollover (Feb 29 -> Mar 1) is the result, same as "same date last year"
 * tools generally default to.
 */
export const priorYearRange = (start: string, end: string): { start: string; end: string } => {
  const priorStart = new Date(`${start}T00:00:00`);
  priorStart.setFullYear(priorStart.getFullYear() - 1);
  const priorEnd = new Date(`${end}T00:00:00`);
  priorEnd.setFullYear(priorEnd.getFullYear() - 1);
  return { start: ymd(priorStart), end: ymd(priorEnd) };
};

/**
 * Custom dimension `field` → the WidgetFilterKey its bucket values translate
 * into. Only enum-like canonical fields are listed — their filter `value` is
 * the raw warehouse value itself (see useFilterOptions.ts), so a bucket's
 * `values` can be sent directly as that filter's ID array with zero backend
 * change. Entity-name fields (advertiser_name, campaign_name, line_item_name)
 * use real numeric database IDs and aren't supported here.
 */
export const CUSTOM_DIMENSION_FILTER_TARGETS: Partial<Record<string, WidgetFilterKey>> = {
  channel: 'channelIds',
  dsp: 'dsps',
  device: 'deviceIds',
  product: 'productIds',
  publisher: 'publishers',
};

/**
 * Expand selected custom-dimension buckets into the underlying filter arrays
 * they stand for. A dimension with no active selection, or built on an
 * unsupported field, contributes nothing.
 */
export function expandCustomDimensionFilters(
  selections: Record<string, string[]>,
  dimensions: CustomDimensionDef[],
): Partial<Record<WidgetFilterKey, string[]>> {
  const out: Partial<Record<WidgetFilterKey, string[]>> = {};
  for (const dim of dimensions) {
    const target = CUSTOM_DIMENSION_FILTER_TARGETS[dim.field];
    const selectedLabels = selections[dim.key];
    if (!target || !selectedLabels?.length) continue;
    // resolveBucket (tenantFields.ts) assigns a row to the FIRST bucket whose
    // values contain it, so an overlapping value belongs only to that earlier
    // bucket. Claim values in the same order here — otherwise selecting a
    // later bucket that happens to share a value pulls in rows that actually
    // bucket to the earlier one.
    const normalize = (v: string) => v.trim().toLowerCase();
    const claimed = new Set<string>();
    const values: string[] = [];
    for (const bucket of dim.buckets) {
      const owned = bucket.values.filter(v => !claimed.has(normalize(v)));
      for (const v of bucket.values) claimed.add(normalize(v));
      if (selectedLabels.includes(bucket.label)) values.push(...owned);
    }
    if (!values.length) continue;
    out[target] = Array.from(new Set([...(out[target] ?? []), ...values]));
  }
  return out;
}

/** Merge cascading dashboard filters into a widget's report request. */
export function applyDashboardFilters(
  req: Omit<ReportRequest, 'date_range'>,
  f: DashboardFilters,
  customDimensions: CustomDimensionDef[] = [],
): ReportRequest {
  const expanded = expandCustomDimensionFilters(f.customDimensionFilters ?? {}, customDimensions);
  const union = (key: WidgetFilterKey): string[] => Array.from(new Set([...(f[key] ?? []), ...(expanded[key] ?? [])]));

  const dsps = union('dsps');
  const channelIds = union('channelIds');
  const deviceIds = union('deviceIds');
  const productIds = union('productIds');
  const publishers = union('publishers');

  return {
    ...req,
    date_range: { start: f.dateStart, end: f.dateEnd },
    ...(dsps.length ? { dsps } : {}),
    ...(f.whitelabelIds.length ? { whitelabel_ids: f.whitelabelIds } : {}),
    ...(f.clientIds.length ? { client_ids: f.clientIds } : {}),
    ...(f.advertiserIds.length ? { advertiser_ids: f.advertiserIds } : {}),
    // Ribeye entity ids (#4614): one selection spans the DSP campaigns / line
    // items on every DSP. Never alongside campaign_ids / line_item_ids — both
    // would AND. Keyword terms and groups below still match names.
    ...(f.campaignIds.length ? { ribeye_campaign_ids: f.campaignIds.map(Number) } : {}),
    ...(f.lineItemIds.length ? { ribeye_line_item_ids: f.lineItemIds.map(Number) } : {}),
    ...(channelIds.length ? { channel_ids: channelIds } : {}),
    ...(deviceIds.length ? { device_ids: deviceIds } : {}),
    ...(productIds.length ? { product_ids: productIds } : {}),
    ...(f.creativeIds.length ? { creative_ids: f.creativeIds } : {}),
    ...(publishers.length ? { publishers } : {}),
    ...(Object.keys(f.containsFilters ?? {}).length ? { contains: containsRequestFilters(f.containsFilters) } : {}),
    ...(Object.keys(f.excludesFilters ?? {}).length ? { excludes: containsRequestFilters(f.excludesFilters) } : {}),
    ...(f.booleanGroups?.length
      ? (() => {
          const groups = booleanGroupsToRequest(f.booleanGroups);
          return groups.length ? { groups } : {};
        })()
      : {}),
  };
}

/**
 * Reverse of Unified Reporting's `deviceLabel` display relabelling
 * (`DEVICE_DISPLAY` in ReportingV2/format.ts) — keyed lower-case so a typed
 * "ctv"/"CTV"/"Ctv"/"Connected TV" all resolve the same way the backend's own
 * case-insensitive REGEXP_LIKE match would.
 *
 * Keyword/Advanced-Filter-Builder device terms are free text, and "CTV" is
 * the only label a user has ever seen for this bucket (the Unified Reporting
 * card) — but the `device_ids` filter matches the RAW `DEVICES.NAME` column
 * (`ribeye-api/app/Services/FlankReporting/queryBuilder.ts`), which has no row
 * literally named "CTV" (seeded as TV/Mobile/Tablet/Desktop/Games Console).
 * "Device contains CTV" was therefore a guaranteed zero-row match (flank#4882).
 * Expand a display term to every raw NAME that produces it so `contains`/`is`
 * matches the bucket a user would actually recognise.
 *
 * Derived from `DEVICE_DISPLAY` — the single source of truth for this
 * relabelling — instead of hand-maintained separately, so a missed alias
 * ("Connected TV" originally wasn't keyed here, a real zero-result bug) can't
 * drift out of sync with it again. Keys both the display name ("ctv") and
 * every raw value that produces it ("tv", "connected tv"), each mapping to
 * the full raw set for that display bucket.
 */
const DEVICE_DISPLAY_TO_RAW: Record<string, string[]> = Object.entries(DEVICE_DISPLAY).reduce(
  (acc, [raw, display]) => {
    const group = (acc[display.toLowerCase()] ??= []);
    group.push(raw);
    acc[raw.toLowerCase()] = group;
    return acc;
  },
  {} as Record<string, string[]>,
);

/** Applies the device display->raw expansion above, only to `deviceIds` terms. */
const normalizeFilterTerms = (key: ContainsFilterKey, terms: string[]): string[] =>
  key === 'deviceIds' ? terms.flatMap(term => DEVICE_DISPLAY_TO_RAW[term.trim().toLowerCase()] ?? [term]) : terms;

/**
 * Boolean groups (camelCase keys) → the backend's snake_case FilterGroup
 * shape. A condition the user hasn't entered a value for yet (or a group
 * left with none) is dropped rather than sent — the backend's `values`
 * field requires at least one entry, and "Add condition"/"Add condition
 * group" both start empty, so building a group is otherwise impossible
 * without a mid-edit 422 (STORM review, flank#4515).
 */
const booleanGroupsToRequest = (groups: BooleanFilterGroup[]): FilterGroup[] =>
  groups
    .map(group => ({
      op: group.op,
      conditions: group.conditions
        .filter(condition => condition.values.length > 0)
        .map(condition => ({
          op: condition.op,
          values: normalizeFilterTerms(condition.key, condition.values),
          key: CONTAINS_KEY_TO_REQUEST_KEY[condition.key],
        })),
    }))
    .filter(group => group.conditions.length > 0);

/** WidgetFilterKey (camelCase) → the backend's snake_case FilterColumnKey. */
const CONTAINS_KEY_TO_REQUEST_KEY: Record<ContainsFilterKey, keyof NonNullable<ReportRequest['contains']>> = {
  dsps: 'dsps',
  advertiserIds: 'advertiser_ids',
  campaignIds: 'campaign_ids',
  lineItemIds: 'line_item_ids',
  channelIds: 'channel_ids',
  deviceIds: 'device_ids',
  productIds: 'product_ids',
  creativeIds: 'creative_ids',
  publishers: 'publishers',
};

const containsRequestFilters = (
  filters: Partial<Record<ContainsFilterKey, string[]>>,
): NonNullable<ReportRequest['contains']> => {
  const out: NonNullable<ReportRequest['contains']> = {};
  for (const [key, terms] of Object.entries(filters) as Array<[ContainsFilterKey, string[] | undefined]>) {
    if (terms?.length) out[CONTAINS_KEY_TO_REQUEST_KEY[key]] = normalizeFilterTerms(key, terms);
  }
  return out;
};

// ── per-widget filter overrides ("Extend · N inherited") ────────────────────

/** The dimensions a widget may override. Date range stays dashboard-level. */
export type WidgetFilterKey =
  | 'clientIds'
  | 'advertiserIds'
  | 'campaignIds'
  | 'lineItemIds'
  | 'channelIds'
  | 'dsps'
  | 'deviceIds'
  | 'productIds'
  | 'creativeIds'
  | 'publishers';

export const WIDGET_FILTER_KEYS: Array<{ key: WidgetFilterKey; label: string; dimension: string }> = [
  { key: 'clientIds', label: 'Client', dimension: 'client' },
  { key: 'advertiserIds', label: 'Advertiser', dimension: 'advertiser' },
  { key: 'campaignIds', label: 'Campaign', dimension: 'ribeye_campaign' },
  { key: 'lineItemIds', label: 'Line Item', dimension: 'ribeye_line_item' },
  { key: 'channelIds', label: 'Channel', dimension: 'channel' },
  // Campaign / Line Item / DSP / Channel are the four dimensions flank#3966
  // requirement 3 names explicitly, so DSP has to be overridable per widget
  // and not just at dashboard level.
  { key: 'dsps', label: 'DSP', dimension: 'dsp' },
  { key: 'deviceIds', label: 'Device', dimension: 'device' },
  // Product removed (flank#5059) — see the FILTER_BAR_ITEMS comment above.
  { key: 'creativeIds', label: 'Creative', dimension: 'creative' },
  { key: 'publishers', label: 'Publisher', dimension: 'publisher' },
];

export type WidgetFilters = Partial<Record<WidgetFilterKey, string[]>>;

/**
 * Resolve the effective filters for one widget: it inherits every dashboard
 * filter, and any dimension it sets itself REPLACES the inherited value for
 * that dimension (an intersection would usually yield an empty result set).
 * A dashboard-level contains/excludes term on that same dimension would
 * otherwise survive and AND against the override, so it's cleared too.
 */
export const resolveWidgetFilters = (
  dashboard: DashboardFilters,
  widget?: WidgetFilters,
  customDimensions: CustomDimensionDef[] = [],
): DashboardFilters => {
  if (!widget) return dashboard;
  const merged = {
    ...dashboard,
    containsFilters: { ...dashboard.containsFilters },
    excludesFilters: { ...dashboard.excludesFilters },
    customDimensionFilters: { ...dashboard.customDimensionFilters },
  };
  for (const { key } of WIDGET_FILTER_KEYS) {
    const own = widget[key];
    if (!own?.length) continue;
    merged[key] = own;
    if (key !== 'clientIds') {
      delete merged.containsFilters[key];
      delete merged.excludesFilters[key];
      // A dashboard-level custom-dimension selection targeting this same
      // underlying field would otherwise survive resolveWidgetFilters and get
      // unioned back into the request by applyDashboardFilters, silently
      // widening the widget's override right back out (CodeRabbit, #4436).
      for (const dim of customDimensions) {
        if (CUSTOM_DIMENSION_FILTER_TARGETS[dim.field] === key) delete merged.customDimensionFilters[dim.key];
      }
    }
  }
  return merged;
};

/** The permission flags that gate a `WidgetFilterKey` out of every picker —
 * DSP (`canViewPlatform`, #4442) and Publisher (`canViewRawInventory`,
 * flank#5097) — keyed by the same field `derivePermissions` exposes, so a
 * denied flag's key is simply absent from `WidgetFilterGatePermissions`. */
export type WidgetFilterGatePermissions = { canViewPlatform: boolean; canViewRawInventory: boolean };

const WIDGET_FILTER_GATES: Partial<Record<WidgetFilterKey, keyof WidgetFilterGatePermissions>> = {
  dsps: 'canViewPlatform',
  publishers: 'canViewRawInventory',
};

/**
 * The widget-overridable dimensions this dashboard actually offers. A filter set
 * to Hidden is gone from the bar, so offering it as a per-widget override would
 * reintroduce exactly the control the dashboard chose to remove. Both surfaces
 * gate on the same config (flank#3966 slice F).
 *
 * Also drops DSP/Publisher for a viewer without the matching permission
 * (flank#5097) — same server rule `AdvancedFilterBuilder`'s DIMENSION_OPTIONS
 * now applies, so a widget-filter override can't offer a dimension the
 * dashboard-level bar already hides for the same reason.
 */
export const availableWidgetFilterKeys = (cfg: FilterBarConfig, permissions: WidgetFilterGatePermissions) =>
  WIDGET_FILTER_KEYS.filter(({ key }) => (cfg.placement[key] ?? 'pinned') !== 'hidden').filter(({ key }) => {
    const gate = WIDGET_FILTER_GATES[key];
    return !gate || permissions[gate];
  });

/** How many dashboard dimensions this widget inherits (i.e. doesn't override). */
export const inheritedCount = (dashboard: DashboardFilters, widget?: WidgetFilters): number =>
  WIDGET_FILTER_KEYS.filter(({ key }) => (dashboard[key] ?? []).length > 0 && !widget?.[key]?.length).length;

/** How many dimensions this widget overrides itself. */
export const overrideCount = (widget?: WidgetFilters): number =>
  WIDGET_FILTER_KEYS.filter(({ key }) => widget?.[key]?.length).length;

/** Stable signature so widget effects refetch when any cascading filter changes. */
export const filtersSignature = (f: DashboardFilters): string => {
  // Defensive: filters can come from stored config written before a key existed.
  const j = (v?: string[]) => (v ?? []).join('|');
  // JSON.stringify (not a delimiter join like `j` above) — a keyword term
  // containing a literal '|' would otherwise collide with a differently-split
  // term list, e.g. ['a|b'] vs ['a', 'b'], producing an identical signature
  // and letting a stale widget fetch survive a real filter change.
  const jMap = (m?: Partial<Record<string, string[]>>) =>
    JSON.stringify(
      Object.entries(m ?? {})
        .filter(([, terms]) => terms?.length)
        .sort(([left], [right]) => left.localeCompare(right)),
    );
  return [
    f.dateStart,
    f.dateEnd,
    j(f.dsps),
    j(f.whitelabelIds),
    j(f.clientIds),
    j(f.advertiserIds),
    j(f.campaignIds),
    j(f.lineItemIds),
    j(f.channelIds),
    j(f.deviceIds),
    j(f.productIds),
    j(f.creativeIds),
    j(f.publishers),
    jMap(f.containsFilters),
    jMap(f.excludesFilters),
    jMap(f.customDimensionFilters),
    // The request-effective shape (booleanGroupsToRequest), not the raw UI
    // state: an empty draft group/condition or a different UI-only `id`
    // changes this JSON without changing the request applyDashboardFilters
    // actually sends, so using raw f.booleanGroups reran widget fetches and
    // marked a saved view "modified" for no real filter change (CodeRabbit,
    // flank#4515).
    JSON.stringify(booleanGroupsToRequest(f.booleanGroups ?? [])),
  ].join('~');
};

// ── persistence ──────────────────────────────────────────────────────────────
// Filters live in the dashboard's own `config` column. Dashboards saved before
// that column existed kept them in a reserved sentinel entry inside `layout`;
// `splitLayout` still honours that so those dashboards keep their filters, and
// they migrate to `config` on the next save.
export const DASHBOARD_CONFIG_TYPE = '__dashboard_config__';

export const splitLayout = (
  layout: Array<Record<string, unknown>>,
  config?: Record<string, unknown> | null,
): { filters: DashboardFilters; widgets: Array<Record<string, unknown>> } => {
  const widgets = layout.filter(w => w?.type !== DASHBOARD_CONFIG_TYPE);
  const legacy = layout.find(w => w?.type === DASHBOARD_CONFIG_TYPE);
  const stored = (config?.filters ?? legacy?.filters ?? {}) as Partial<DashboardFilters>;
  return { filters: normalizeStoredFilters(stored), widgets };
};

// ── ribeye entity ids (#4614) ────────────────────────────────────────────────
// campaignIds / lineItemIds hold ribeye entity ids. Dashboards saved before that
// hold DSP names there, which can't become ids, so they're dropped on load and
// the user is asked to reselect. A marker rather than a "looks like an id" test:
// a DSP name can be all digits.
export const ENTITY_FILTERS_MARKER = 'ribeye';

/**
 * Clear legacy campaign / line item names from every place a dashboard stores a
 * selection: its filters, the pre-`config` sentinel entry in `layout`, each saved
 * view and each widget override. Keyword terms and boolean groups still match
 * names, so they stay. `cleared` says whether anything non-empty was dropped.
 */
export const migrateEntityFilters = (
  layout: Array<Record<string, unknown>>,
  config: Record<string, unknown> | null | undefined,
): {
  layout: Array<Record<string, unknown>>;
  config: Record<string, unknown> | null | undefined;
  cleared: boolean;
} => {
  if (config?.entityFilters === ENTITY_FILTERS_MARKER) return { layout, config, cleared: false };

  let cleared = false;
  const strip = (filters: unknown): unknown => {
    if (!filters || typeof filters !== 'object') return filters;
    const stored = filters as Record<string, unknown>;
    const held = (key: string) => Array.isArray(stored[key]) && (stored[key] as unknown[]).length > 0;
    if (!held('campaignIds') && !held('lineItemIds')) return filters;
    cleared = true;
    return { ...stored, campaignIds: [], lineItemIds: [] };
  };

  const nextLayout = layout.map(entry => {
    if (!entry || typeof entry !== 'object') return entry;
    if (entry.type === DASHBOARD_CONFIG_TYPE) return { ...entry, filters: strip(entry.filters) };
    return entry.widgetFilters ? { ...entry, widgetFilters: strip(entry.widgetFilters) } : entry;
  });
  const nextConfig = config && {
    ...config,
    ...(config.filters ? { filters: strip(config.filters) } : {}),
    ...(Array.isArray(config.views)
      ? {
          views: config.views.map(view =>
            view && typeof view === 'object'
              ? { ...view, filters: strip((view as { filters?: unknown }).filters) }
              : view,
          ),
        }
      : {}),
  };
  return { layout: nextLayout, config: nextConfig, cleared };
};

/**
 * Clear a selected Product filter from every place a dashboard stores one: its
 * filters, the pre-`config` sentinel entry in `layout`, each saved view and
 * each widget override — same shape as `migrateEntityFilters` above, for the
 * filter flank#5059 removed from the catalog. Only a non-empty `productIds`
 * counts as "held", so a dashboard that never used the filter reports
 * `cleared: false` and shows no notice.
 */
export const migrateProductFilter = (
  layout: Array<Record<string, unknown>>,
  config: Record<string, unknown> | null | undefined,
): {
  layout: Array<Record<string, unknown>>;
  config: Record<string, unknown> | null | undefined;
  cleared: boolean;
} => {
  let cleared = false;
  const strip = (filters: unknown): unknown => {
    if (!filters || typeof filters !== 'object') return filters;
    const stored = filters as Record<string, unknown>;
    if (!Array.isArray(stored.productIds) || stored.productIds.length === 0) return filters;
    cleared = true;
    return { ...stored, productIds: [] };
  };

  const nextLayout = layout.map(entry => {
    if (!entry || typeof entry !== 'object') return entry;
    if (entry.type === DASHBOARD_CONFIG_TYPE) return { ...entry, filters: strip(entry.filters) };
    return entry.widgetFilters ? { ...entry, widgetFilters: strip(entry.widgetFilters) } : entry;
  });
  const nextConfig = config && {
    ...config,
    ...(config.filters ? { filters: strip(config.filters) } : {}),
    ...(Array.isArray(config.views)
      ? {
          views: config.views.map(view =>
            view && typeof view === 'object'
              ? { ...view, filters: strip((view as { filters?: unknown }).filters) }
              : view,
          ),
        }
      : {}),
  };
  return { layout: nextLayout, config: nextConfig, cleared };
};

/**
 * Drop a widget whose own group-by (or pivot column) dimension was Product
 * (flank#5100) — unlike `migrateProductFilter` above (the FILTER flank#5059
 * removed, where clearing the selection to empty leaves a sensible widget
 * behind), a widget whose entire purpose IS the Product breakdown has
 * nothing left to show once that dimension is gone, and would otherwise
 * silently fall back to the DSP/Channel default instead of disappearing.
 * Delivery not launched through Flank has no product, so this groups the
 * bulk of delivery under "Unmapped" — Unified Reporting dropped the same
 * breakdown for the same reason (#4109).
 *
 * Also clears a Product-backed custom dimension's own saved BUCKET SELECTION
 * (`customDimensionFilters[dim.key]`, from the dashboard filters, every saved
 * view, and every widget override) — unlike a group-by usage, this survives
 * the dimension's own removal: `expandCustomDimensionFilters`/
 * `applyDashboardFilters` still expands it into `productIds` on every
 * request, so a dashboard would keep silently filtering by product forever
 * even after its last Product widget is gone (CodeRabbit).
 */
export const migrateProductDimension = (
  layout: Array<Record<string, unknown>>,
  config: Record<string, unknown> | null | undefined,
  // Saved tenant-dimension keys whose underlying field is `product` — a
  // custom dimension bucketed on Product is the same breakdown by another
  // name (CodeRabbit, PR #5109).
  productDimensionKeys: ReadonlySet<string> = new Set(),
): {
  layout: Array<Record<string, unknown>>;
  config: Record<string, unknown> | null | undefined;
  cleared: boolean;
} => {
  const isProductWidget = (entry: Record<string, unknown>) =>
    entry.dimension === 'Product' ||
    entry.columnDimension === 'Product' ||
    productDimensionKeys.has(entry.dimension as string) ||
    productDimensionKeys.has(entry.columnDimension as string);
  const droppedLayout = layout.filter(
    entry => !entry || typeof entry !== 'object' || entry.type === DASHBOARD_CONFIG_TYPE || !isProductWidget(entry),
  );

  let selectionsCleared = false;
  const stripSelections = (filters: unknown): unknown => {
    if (!filters || typeof filters !== 'object') return filters;
    const stored = filters as Record<string, unknown>;
    const selections = stored.customDimensionFilters as Record<string, string[]> | undefined;
    if (!selections) return filters;
    const nextSelections = { ...selections };
    let changed = false;
    for (const key of productDimensionKeys) {
      if (nextSelections[key]?.length) {
        delete nextSelections[key];
        changed = true;
      }
    }
    if (!changed) return filters;
    selectionsCleared = true;
    return { ...stored, customDimensionFilters: nextSelections };
  };

  const nextLayout = droppedLayout.map(entry => {
    if (!entry || typeof entry !== 'object') return entry;
    if (entry.type === DASHBOARD_CONFIG_TYPE) return { ...entry, filters: stripSelections(entry.filters) };
    return entry.widgetFilters ? { ...entry, widgetFilters: stripSelections(entry.widgetFilters) } : entry;
  });
  const nextConfig = config && {
    ...config,
    ...(config.filters ? { filters: stripSelections(config.filters) } : {}),
    ...(Array.isArray(config.views)
      ? {
          views: config.views.map(view =>
            view && typeof view === 'object'
              ? { ...view, filters: stripSelections((view as { filters?: unknown }).filters) }
              : view,
          ),
        }
      : {}),
  };

  return {
    layout: nextLayout,
    config: nextConfig,
    cleared: droppedLayout.length !== layout.length || selectionsCleared,
  };
};

/**
 * Clear a DSP or Publisher condition a viewer no longer has permission to use
 * (flank#5097) — from the dashboard filters, every saved view, every widget
 * override, and (unlike `migrateEntityFilters`/`migrateProductFilter`) the
 * contains/excludes keyword terms and Boolean-group conditions on that same
 * key too: those still match by name for a stale entity ID, but a permission
 * denial means the viewer can't use that dimension in ANY form, wildcard or
 * exact. Same raw-JSON shape as the two migrations above, run alongside them
 * in the load effect.
 */
export const stripDeniedFilterConditions = (
  layout: Array<Record<string, unknown>>,
  config: Record<string, unknown> | null | undefined,
  permissions: WidgetFilterGatePermissions,
): {
  layout: Array<Record<string, unknown>>;
  config: Record<string, unknown> | null | undefined;
  cleared: boolean;
} => {
  const deniedKeys = (Object.keys(WIDGET_FILTER_GATES) as WidgetFilterKey[]).filter(
    key => !permissions[WIDGET_FILTER_GATES[key] as keyof WidgetFilterGatePermissions],
  );
  if (deniedKeys.length === 0) return { layout, config, cleared: false };

  let cleared = false;
  const omit = (obj: Record<string, unknown>, key: string): Record<string, unknown> => {
    const { [key]: _omit, ...rest } = obj;
    return rest;
  };
  const strip = (filters: unknown): unknown => {
    if (!filters || typeof filters !== 'object') return filters;
    let next = filters as Record<string, unknown>;
    for (const key of deniedKeys) {
      if (Array.isArray(next[key]) && (next[key] as unknown[]).length > 0) {
        cleared = true;
        next = { ...next, [key]: [] };
      }
      const containsFilters = next.containsFilters as Record<string, unknown> | undefined;
      if (containsFilters && key in containsFilters) {
        cleared = true;
        next = { ...next, containsFilters: omit(containsFilters, key) };
      }
      const excludesFilters = next.excludesFilters as Record<string, unknown> | undefined;
      if (excludesFilters && key in excludesFilters) {
        cleared = true;
        next = { ...next, excludesFilters: omit(excludesFilters, key) };
      }
      const booleanGroups = next.booleanGroups as Array<{ conditions: Array<{ key: string }> }> | undefined;
      if (Array.isArray(booleanGroups) && booleanGroups.some(g => g.conditions?.some(c => c.key === key))) {
        cleared = true;
        next = {
          ...next,
          booleanGroups: booleanGroups
            .map(g => ({ ...g, conditions: g.conditions.filter(c => c.key !== key) }))
            .filter(g => g.conditions.length > 0),
        };
      }
    }
    return next;
  };

  const nextLayout = layout.map(entry => {
    if (!entry || typeof entry !== 'object') return entry;
    if (entry.type === DASHBOARD_CONFIG_TYPE) return { ...entry, filters: strip(entry.filters) };
    return entry.widgetFilters ? { ...entry, widgetFilters: strip(entry.widgetFilters) } : entry;
  });
  const nextConfig = config && {
    ...config,
    ...(config.filters ? { filters: strip(config.filters) } : {}),
    ...(Array.isArray(config.views)
      ? {
          views: config.views.map(view =>
            view && typeof view === 'object'
              ? { ...view, filters: strip((view as { filters?: unknown }).filters) }
              : view,
          ),
        }
      : {}),
  };
  return { layout: nextLayout, config: nextConfig, cleared };
};

/**
 * Clear selections the new scope can no longer contain. Campaign / Line Item
 * options are scoped by Client and Advertiser (and Line Item by Campaign), so a
 * stale child selection would otherwise zero every widget with no row left to
 * untick — same rule as Unified Reporting's slice. A patch that sets the child
 * itself (saved view, reset) keeps it.
 */
export const withEntityCascade = (
  prev: Partial<DashboardFilters>,
  patch: Partial<DashboardFilters>,
): Partial<DashboardFilters> => {
  const changed = (key: 'whitelabelIds' | 'clientIds' | 'advertiserIds' | 'campaignIds') =>
    patch[key] !== undefined && JSON.stringify(patch[key]) !== JSON.stringify(prev[key]);
  const next = { ...patch };
  // White Label scopes Campaign/Line Item the same way Client/Advertiser do
  // (flank#5058) — same reasoning Unified Reporting's own cascade uses (#4614):
  // a stale selection the new scope can no longer contain would otherwise
  // zero every widget with no row left to untick.
  if (changed('whitelabelIds') || changed('clientIds') || changed('advertiserIds')) {
    if (patch.campaignIds === undefined) next.campaignIds = [];
    if (patch.lineItemIds === undefined) next.lineItemIds = [];
  }
  if (changed('campaignIds') && patch.lineItemIds === undefined) next.lineItemIds = [];
  return next;
};

/** A widget's own override change, under the same child-clearing rule as the filter bar. */
export const withWidgetOverride = (prev: WidgetFilters, key: WidgetFilterKey, values: string[]): WidgetFilters => ({
  ...prev,
  ...withEntityCascade(prev, { [key]: values }),
});

/**
 * What a widget override picker's options are narrowed by: Campaign / Line Item
 * follow the widget's effective parents (its own overrides, else the dashboard's),
 * so an override can't pick an entity those parents rule out. The rest list unscoped.
 */
export const widgetEntityScope = (key: WidgetFilterKey, effective: DashboardFilters): OptionsScope => {
  if (key !== 'campaignIds' && key !== 'lineItemIds') return {};
  const parents = {
    whitelabels: effective.whitelabelIds,
    clients: effective.clientIds,
    advertisers: effective.advertiserIds,
  };
  return key === 'lineItemIds' ? { ...parents, ribeyeCampaignIds: effective.campaignIds } : parents;
};

// ── filter-bar configuration ─────────────────────────────────────────────────
// Which filters this dashboard's bar offers, and where. Mirrors FLANK 2.0's
// "Filter bar" drawer: a dashboard handed to one client rarely needs all ten
// controls, and the ones it does need should lead. Stored in `config` so it
// travels with the dashboard.

/** `pinned` sits on the bar; `more` hides behind "Add filter"; `hidden` is gone. */
export type FilterPlacement = 'pinned' | 'more' | 'hidden';

/**
 * The catalog, in default order. `key` is the DashboardFilters field the control
 * writes, except 'date' which owns dateStart/dateEnd/datePreset together.
 * `defaultPlacement` reproduces the bar exactly as it shipped, so a dashboard
 * with no stored config looks unchanged.
 */
export const FILTER_BAR_ITEMS: Array<{ key: string; label: string; defaultPlacement: FilterPlacement }> = [
  { key: 'date', label: 'Date range', defaultPlacement: 'pinned' },
  // Admin-only (flank#5058, same role gate as Unified Reporting's #4454) —
  // the catalog entry is unconditional, like every other key here, since
  // DashboardFilterBar.tsx is what actually hides the control for a
  // non-admin viewer regardless of this dashboard's own placement config.
  { key: 'whitelabelIds', label: 'White Label', defaultPlacement: 'pinned' },
  { key: 'clientIds', label: 'Client', defaultPlacement: 'pinned' },
  { key: 'advertiserIds', label: 'Advertiser', defaultPlacement: 'pinned' },
  { key: 'campaignIds', label: 'Campaign', defaultPlacement: 'pinned' },
  { key: 'lineItemIds', label: 'Line Item', defaultPlacement: 'pinned' },
  { key: 'channelIds', label: 'Channel', defaultPlacement: 'pinned' },
  // Product removed from the filter catalog (flank#5059): it's only set on
  // line items launched through Flank, so delivery from any other source has
  // none, and a Product filter silently left it out. `productIds` stays on
  // `DashboardFilters` itself (see `defaultDashboardFilters`) so an existing
  // selection can still be stripped on load (`migrateProductFilter` below) and
  // so "Group by Product" (a dimension, not a filter — WIDGET_DIMENSIONS,
  // untouched) keeps working; only the FILTER catalog entries are gone, here
  // and in WIDGET_FILTER_KEYS below.
  { key: 'deviceIds', label: 'Device', defaultPlacement: 'pinned' },
  { key: 'dsps', label: 'DSP', defaultPlacement: 'pinned' },
  { key: 'creativeIds', label: 'Creative', defaultPlacement: 'more' },
  { key: 'publishers', label: 'Publisher', defaultPlacement: 'more' },
];

export interface FilterBarConfig {
  /** Display order, by key. */
  order: string[];
  placement: Record<string, FilterPlacement>;
}

export const defaultFilterBarConfig = (): FilterBarConfig => ({
  order: FILTER_BAR_ITEMS.map(i => i.key),
  placement: Object.fromEntries(FILTER_BAR_ITEMS.map(i => [i.key, i.defaultPlacement])),
});

/**
 * Read the stored config, reconciled against the current catalog. A dashboard
 * saved before a filter existed must not hide it — unknown keys are dropped and
 * missing ones appended at their default placement, so adding a filter to the
 * catalog surfaces it everywhere instead of silently disappearing.
 */
export const readFilterBarConfig = (config?: Record<string, unknown> | null): FilterBarConfig => {
  const stored = config?.filterBar as Partial<FilterBarConfig> | undefined;
  const known = new Set(FILTER_BAR_ITEMS.map(i => i.key));
  const storedOrder = Array.isArray(stored?.order) ? stored.order.filter(k => known.has(k)) : [];
  const order = [...storedOrder, ...FILTER_BAR_ITEMS.map(i => i.key).filter(k => !storedOrder.includes(k))];

  const placement = { ...defaultFilterBarConfig().placement };
  for (const item of FILTER_BAR_ITEMS) {
    const p = stored?.placement?.[item.key];
    if (p === 'pinned' || p === 'more' || p === 'hidden') placement[item.key] = p;
  }
  return { order, placement };
};

/**
 * Slice F resolver: dashboard config → tenant default → built-in default.
 *
 * The tenant default is an ALLOWLIST of enabled keys, and it is a ceiling rather
 * than a starting point — anything a whitelabel admin has switched off is forced
 * to `hidden` and a dashboard cannot re-enable it. Within what the tenant allows,
 * the dashboard's own placement wins.
 *
 * `null` means the tenant has no opinion, which is NOT the same as `[]`: an
 * admin who disables every filter gets an empty bar, whereas a tenant that has
 * never been configured gets the built-in default.
 */
export const resolveFilterBarConfig = (
  config?: Record<string, unknown> | null,
  tenantEnabledKeys?: string[] | null,
): FilterBarConfig => {
  const dashboard = readFilterBarConfig(config);
  if (!tenantEnabledKeys) return dashboard;

  const allowed = new Set(tenantEnabledKeys);
  const placement = { ...dashboard.placement };
  for (const item of FILTER_BAR_ITEMS) {
    if (!allowed.has(item.key)) placement[item.key] = 'hidden';
  }
  return { ...dashboard, placement };
};

/** The keys a dashboard currently offers — what "Save as tenant default" stores. */
export const enabledFilterKeys = (cfg: FilterBarConfig): string[] =>
  FILTER_BAR_ITEMS.map(i => i.key).filter(k => (cfg.placement[k] ?? 'pinned') !== 'hidden');

/** The catalog in this dashboard's order, carrying each item's placement. */
export const orderedFilterBarItems = (cfg: FilterBarConfig) =>
  cfg.order
    .map(key => {
      const item = FILTER_BAR_ITEMS.find(i => i.key === key);
      return item ? { ...item, placement: cfg.placement[key] ?? item.defaultPlacement } : null;
    })
    .filter((i): i is (typeof FILTER_BAR_ITEMS)[number] & { placement: FilterPlacement } => i !== null);

/** Move one filter up or down in the order, returning a new config. */
export const moveFilter = (cfg: FilterBarConfig, key: string, delta: -1 | 1): FilterBarConfig => {
  const order = [...cfg.order];
  const from = order.indexOf(key);
  const to = from + delta;
  if (from < 0 || to < 0 || to >= order.length) return cfg;
  [order[from], order[to]] = [order[to], order[from]];
  return { ...cfg, order };
};

// ── saved views ──────────────────────────────────────────────────────────────
// A view is a named snapshot of the dashboard filters, so a dashboard can be
// re-read through several lenses (e.g. "CTV only", "Q3 all channels") without
// rebuilding the filter bar each time. Stored alongside filters in `config`.

export interface SavedView {
  id: string;
  name: string;
  filters: DashboardFilters;
}

export const readSavedViews = (config?: Record<string, unknown> | null): SavedView[] => {
  const raw = config?.views;
  if (!Array.isArray(raw)) return [];
  return (
    raw
      .filter(
        (v): v is SavedView =>
          typeof v === 'object' && v !== null && typeof (v as SavedView).id === 'string' && !!(v as SavedView).filters,
      )
      // A view snapshot was written with whatever filter keys existed at the time,
      // so merge over the current defaults — otherwise a key added later reads as
      // undefined everywhere the snapshot is used.
      .map(v => ({ ...v, filters: normalizeStoredFilters(v.filters) }))
  );
};

/** True when `filters` differs from the view it was loaded from (drives "modified"). */
export const filtersDifferFrom = (filters: DashboardFilters, view?: SavedView | null): boolean =>
  !!view && filtersSignature(filters) !== filtersSignature(view.filters);

/** The `config` payload to persist alongside the layout. */
export const buildConfig = (
  filters: DashboardFilters,
  views: SavedView[] = [],
  activeViewId: string | null = null,
  branding: DashboardBranding = {},
  filterBar: FilterBarConfig = defaultFilterBarConfig(),
): Record<string, unknown> => ({
  filters,
  views,
  activeViewId,
  branding,
  filterBar,
  entityFilters: ENTITY_FILTERS_MARKER,
});

// ── branding ─────────────────────────────────────────────────────────────────
// Per-dashboard presentation, so a dashboard can be handed to a client looking
// like theirs: a logo, an accent colour used by chart series, and a title.

export interface DashboardBranding {
  logoUrl?: string;
  accentColor?: string;
  headline?: string;
}

export const readBranding = (config?: Record<string, unknown> | null): DashboardBranding => {
  const b = config?.branding;
  if (typeof b !== 'object' || b === null) return {};
  const { logoUrl, accentColor, headline } = b as DashboardBranding;
  return {
    ...(typeof logoUrl === 'string' && logoUrl ? { logoUrl } : {}),
    // only accept a hex colour — this value is interpolated into chart styles
    ...(typeof accentColor === 'string' && /^#[0-9a-fA-F]{6}$/.test(accentColor) ? { accentColor } : {}),
    ...(typeof headline === 'string' && headline ? { headline } : {}),
  };
};

/** Which saved view was selected, if it still exists. */
export const readActiveViewId = (config?: Record<string, unknown> | null, views: SavedView[] = []): string | null => {
  const id = config?.activeViewId;
  return typeof id === 'string' && views.some(v => v.id === id) ? id : null;
};

// ── Channel filter masking (flank#5062) ─────────────────────────────────────
// mergeChannelOptionsForRestrictedViewer (ReportingV2/format.ts) collapses GAM's
// "GAM Display"/"GAM Video" into the same selectable "Display"/"Video" option a
// true Display/Video channel already uses, for a viewer without canViewPlatform
// (flank#4741). Unified Reporting's own filter bar drives chip selection through
// a single-value toggle (toggleChannelSelection); this dashboard's
// MultiSelectFilter instead hands back the whole next-selected array already
// expressed in terms of the merged options' values, so the glue here is the two
// directions that need translating, not the merge itself.

/** `filters.channelIds` (raw warehouse values) collapsed to the merged option's
 * synthetic key, for the chip's own `selected` prop — mirrors
 * ReportingV2FilterBar's `collapsedChannelIds`, deduped the same way so a
 * group covered by two selected raw ids shows one selected chip, not two. */
export const collapseChannelSelection = (selected: string[], channelOptions: ChannelOption[]): string[] => {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const id of selected) {
    const key = channelOptions.find(o => o.values.includes(id))?.value ?? id;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(key);
  }
  return out;
};

/** The reverse direction: `MultiSelectFilter`'s `onChange` hands back the next
 * selected array in terms of the merged options' synthetic keys — expand each
 * back to the raw warehouse values it covers before writing `channelIds`, or a
 * restricted viewer's selection would literally filter by the label string
 * "Display" instead of the channel(s) it stands for.
 *
 * CodeRabbit: `previousSelected` (the current, pre-change `channelIds`) is
 * needed to avoid silently widening a merged group that arrived here only
 * PARTIALLY raw-selected — e.g. a saved view set by an unrestricted viewer to
 * exactly `['display']`, with `gam_display` deliberately left out. Toggling
 * some OTHER chip hands back the WHOLE next-selected array including
 * `Display`'s already-selected key, and re-expanding it from scratch would
 * add `gam_display` even though the viewer never touched that selection. A
 * group's raw ids are only expanded in full the first time it's newly
 * selected; a group that was already (even partially) selected keeps exactly
 * the raw ids it already had. */
export const expandChannelSelection = (
  nextSelected: string[],
  channelOptions: ChannelOption[],
  previousSelected: string[],
): string[] => {
  const prevSet = new Set(previousSelected);
  const out: string[] = [];
  for (const key of nextSelected) {
    const option = channelOptions.find(o => o.value === key);
    const values = !option
      ? [key]
      : option.values.some(v => prevSet.has(v))
        ? option.values.filter(v => prevSet.has(v))
        : option.values;
    for (const v of values) if (!out.includes(v)) out.push(v);
  }
  return out;
};
